import React, { ReactNode } from 'react';
import Checkbox, { CheckboxProps } from '../Checkbox/Checkbox';
import Inline from '../Inline';
import MultiSelectBase, {
  ExtendableMultiSelectBaseProps,
} from '../MultiSelectBase';
import Stack from '../Stack';
import { getNode } from '../_internal/selectBase';
import { MinOptionInfo, NodeOrResolver } from '../_internal/selectBase/types';
import { MarginProps } from '../_internal/spacing';
import {
  getResponsiveValue,
  ResponsiveSizeValue,
  ResponsiveValue,
} from '../_internal/styling';
import useScreenSize from '../_internal/useScreenSize';

/**
 * The info needed to render out each checkbox.
 * More info can be included than can be used in `optionContainer`.
 */
export type OptionInfo<ValueString extends string> =
  MinOptionInfo<ValueString> & {
    /**
     * Checkbox contents. Defaults to `value`.
     */
    label?: NodeOrResolver;
  };

type Container = Required<CheckboxProps>['container'];
type ContainerData = Parameters<Container>[0];
type ContainerFuncReturn = ReturnType<Container>;

interface OptionProps<
  ValueString extends string,
  FullOptionInfo extends OptionInfo<ValueString>,
> {
  /**
   * A render prop that wraps the checkboxes, allowing for placement above and below the button.
   * @param data - The data needed to render the core component behavior. Takes the `value` for each option from `options`.
   * @param currentOption - The current option being processed from `options`.
   * @param index - The index of `currentOption` in `options`.
   */
  optionContainer?: (
    data: ContainerData,
    currentOption: FullOptionInfo,
    index: number,
  ) => ContainerFuncReturn;
}

interface BaseProps {
  /**
   * The size of the checkboxes. This value is responsive and may be configured for multiple screen sizes.
   *
   * @default 'default'
   */
  size?: CheckboxProps['size'];
  /**
   * The orientation of the checkboxes. This value is responsive and may be configured for multiple screen sizes.
   *
   * @default 'horizontal'
   */
  direction?: ResponsiveValue<'horizontal' | 'vertical'>;
  /**
   * The spacing between the checkboxes in the `direction`. This value is responsive and may be configured for multiple screen sizes.
   */
  spacing?: ResponsiveSizeValue;
}

type Props<
  ValueString extends string,
  FullOptionInfo extends OptionInfo<ValueString>,
> = OptionProps<ValueString, FullOptionInfo> & BaseProps & MarginProps;

export type CheckboxGroupProps<
  ValueString extends string,
  FullOptionInfo extends OptionInfo<ValueString>,
> = Omit<
  ExtendableMultiSelectBaseProps<
    ValueString,
    FullOptionInfo,
    Props<ValueString, FullOptionInfo>
  >,
  'children'
>;

const DEFAULT_VERTICAL_SPACING = 1;
const DEFAULT_HORIZONTAL_SPACING = 1.25;

const getContainer = (
  direction: BaseProps['direction'],
  spacing?: ResponsiveSizeValue,
  margin?: MarginProps,
): ((children: ReactNode) => ReactNode) => {
  if (direction === 'vertical') {
    return children => (
      <Stack
        spacing={spacing || DEFAULT_VERTICAL_SPACING}
        {...margin}
        data-testid="checkbox-group"
      >
        {children}
      </Stack>
    );
  }

  return children => (
    <Inline
      spacing={spacing || DEFAULT_HORIZONTAL_SPACING}
      wrap
      {...margin}
      data-testid="checkbox-group"
    >
      {children}
    </Inline>
  );
};

/**
 * A checkbox group is a collection of checkboxes describing a set of related options. Multiple checkboxes in a given group can be selected at the same time.
 */
const CheckboxGroup = <
  ValueString extends string,
  FullOptionInfo extends OptionInfo<ValueString>,
>({
  size,
  direction = 'horizontal',
  spacing,
  optionContainer,
  options,
  m,
  mt,
  mb,
  my,
  ml,
  mr,
  mx,
  ...multiSelectBaseProps
}: CheckboxGroupProps<ValueString, FullOptionInfo>) => {
  const screenSize = useScreenSize();
  const responsiveDirection = getResponsiveValue(
    screenSize,
    direction,
    'horizontal',
  );

  const margin = { m, mt, mb, my, ml, mr, mx } as MarginProps;
  const container = getContainer(responsiveDirection, spacing, margin);

  return (
    <MultiSelectBase
      container={container}
      options={options}
      {...multiSelectBaseProps}
    >
      {({ isSelected, value, ...selectableProps }, currentOption, index) => (
        <Checkbox
          container={
            optionContainer &&
            (containerProps =>
              optionContainer(containerProps, currentOption, index))
          }
          isSelected={isSelected}
          value={value}
          size={size}
          {...selectableProps}
        >
          {getNode(currentOption.label, isSelected) || value}
        </Checkbox>
      )}
    </MultiSelectBase>
  );
};

export default CheckboxGroup;
